<template>
  <div class="duplicate-detection-page">
    <div class="page-header">
      <h2>🔍 Smart Duplicate Detection</h2>
      <p class="page-description">
        AI-powered analysis to identify potential duplicate work orders and purchase orders
      </p>
    </div>

    <!-- Connection Selection -->
    <div class="connection-section">
      <div class="form-group">
        <label>Database Connection</label>
        <select v-model="selectedConnection" class="form-control">
          <option value="">Select a database connection</option>
          <option v-for="conn in connections" :key="conn.id" :value="conn">
            {{ conn.name }} ({{ conn.database_name }})
          </option>
        </select>
      </div>
    </div>

    <!-- Analysis Form -->
    <div class="analysis-form" v-if="selectedConnection">
      <div class="form-row">
        <div class="form-group">
          <label>House Number</label>
          <input 
            v-model="houseNumber"
            type="text" 
            class="form-control"
            placeholder="Enter house number (e.g., 50000554)"
          />
        </div>
        <div class="form-group">
          <label>Analysis Focus</label>
          <select v-model="analysisFocus" class="form-control">
            <option value="general">General Duplicates</option>
            <option value="cost_overruns">Cost Overruns</option>
            <option value="vendor_conflicts">Vendor Conflicts</option>
            <option value="timeline_issues">Timeline Issues</option>
          </select>
        </div>
        <div class="form-group">
          <button 
            @click="analyzeDuplicates"
            class="btn btn-primary"
            :disabled="!houseNumber || analyzing"
          >
            <i class="fas fa-brain mr-2"></i>
            <span v-if="analyzing">Analyzing...</span>
            <span v-else>Analyze Duplicates</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Results Section -->
    <div v-if="results" class="results-section">
      <!-- Summary Cards -->
      <div class="summary-cards">
        <div class="summary-card">
          <div class="card-value">{{ results.duplicates_found || 0 }}</div>
          <div class="card-label">Potential Duplicates</div>
        </div>
        <div class="summary-card">
          <div class="card-value">{{ results.data_summary?.total_records || 0 }}</div>
          <div class="card-label">Records Analyzed</div>
        </div>
        <div class="summary-card">
          <div class="card-value">{{ results.execution_time_ms }}ms</div>
          <div class="card-label">Analysis Time</div>
        </div>
        <div class="summary-card">
          <div class="card-value">${{ formatCurrency(getTotalCostImpact()) }}</div>
          <div class="card-label">Total Cost Impact</div>
        </div>
      </div>

      <!-- Debug Section -->
      <div class="debug-section">
        <details>
          <summary class="debug-toggle">🔍 Debug: Raw Response Data</summary>
          <div class="debug-content">
            <h5>Raw Analysis Response:</h5>
            <pre class="debug-json">{{ results.raw_analysis }}</pre>
            
            <h5>Structured Results:</h5>
            <pre class="debug-json">{{ JSON.stringify(results.structured_results, null, 2) }}</pre>
            
            <h5>Parsed High Confidence:</h5>
            <pre class="debug-json">{{ JSON.stringify(getParsedHighConfidence(), null, 2) }}</pre>
          </div>
        </details>
      </div>

      <!-- Analysis Summary -->
      <div class="analysis-summary">
        <h4>📊 Analysis Summary</h4>
        <p>{{ results.structured_results?.analysis_summary || 'No summary available' }}</p>
      </div>

      <!-- High Confidence Duplicates -->
      <div v-if="getParsedHighConfidence().length > 0" class="duplicates-section high-confidence">
        <h4 class="section-title">
          <i class="fas fa-exclamation-triangle text-danger mr-2"></i>
          High Confidence Duplicates ({{ getParsedHighConfidence().length }})
        </h4>
        
        <div class="duplicate-cards">
          <div 
            v-for="duplicate in getParsedHighConfidence()" 
            :key="duplicate.duplicate_id"
            class="duplicate-card high"
          >
            <!-- Duplicate Header -->
            <div class="duplicate-header">
              <div class="duplicate-title">
                <h5>{{ duplicate.title || 'Potential Duplicate' }}</h5>
                <div class="duplicate-badges">
                  <span class="type-badge">{{ formatDuplicateType(duplicate.type) }}</span>
                  <span class="confidence-badge high">{{ Math.round(duplicate.confidence * 100) }}%</span>
                  <span v-if="duplicate.cost_impact" class="impact-badge">
                    ${{ duplicate.cost_impact.toLocaleString() }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Records Comparison -->
            <div class="records-section">
              <h6>📋 Records Involved</h6>
              <div class="records-grid">
                <div 
                  v-for="(record, index) in duplicate.records_involved" 
                  :key="index" 
                  class="record-card"
                >
                  <div class="record-header">
                    <span class="table-badge">{{ record.table }}</span>
                    <span class="record-id">{{ record.record_id }}</span>
                  </div>
                  <div class="record-fields">
                    <div v-for="(value, field) in record.key_fields" :key="field" class="field-item">
                      <span class="field-label">{{ field }}:</span>
                      <span class="field-value">{{ value }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Analysis Details -->
            <div v-if="duplicate.detailed_reasoning" class="analysis-section">
              <h6>🧠 Analysis Details</h6>
              <div class="analysis-grid">
                <div v-if="duplicate.detailed_reasoning.similarity_analysis" class="analysis-item">
                  <strong>Similarity:</strong>
                  <p>{{ duplicate.detailed_reasoning.similarity_analysis }}</p>
                </div>
                <div v-if="duplicate.detailed_reasoning.differences_found" class="analysis-item">
                  <strong>Differences:</strong>
                  <p>{{ duplicate.detailed_reasoning.differences_found }}</p>
                </div>
                <div v-if="duplicate.detailed_reasoning.timeline_analysis" class="analysis-item">
                  <strong>Timeline:</strong>
                  <p>{{ duplicate.detailed_reasoning.timeline_analysis }}</p>
                </div>
              </div>
              
              <div v-if="duplicate.detailed_reasoning.duplicate_indicators?.length" class="indicators-section">
                <strong>Duplicate Indicators:</strong>
                <ul class="indicators-list">
                  <li v-for="indicator in duplicate.detailed_reasoning.duplicate_indicators" :key="indicator">
                    {{ indicator }}
                  </li>
                </ul>
              </div>
              
              <div v-if="duplicate.detailed_reasoning.risk_factors?.length" class="risks-section">
                <strong>Risk Factors:</strong>
                <ul class="risks-list">
                  <li v-for="risk in duplicate.detailed_reasoning.risk_factors" :key="risk">
                    {{ risk }}
                  </li>
                </ul>
              </div>
            </div>

            <!-- Validation Questions -->
            <div v-if="duplicate.validation_questions?.length" class="validation-section">
              <h6>❓ Validation Questions</h6>
              <div class="questions-list">
                <div v-for="question in duplicate.validation_questions" :key="question" class="question-item">
                  <i class="fas fa-question-circle text-info mr-2"></i>
                  {{ question }}
                </div>
              </div>
            </div>

            <!-- User Validation -->
            <div class="user-validation-section">
              <h6>✅ Your Assessment</h6>
              <div class="validation-controls">
                <div class="validation-buttons">
                  <button 
                    @click="validateDuplicate(duplicate.duplicate_id, 'confirmed')"
                    class="btn btn-sm btn-danger"
                    :class="{ 'active': duplicate.user_validation?.status === 'confirmed' }"
                  >
                    <i class="fas fa-check-circle mr-1"></i>
                    Confirmed
                  </button>
                  <button 
                    @click="validateDuplicate(duplicate.duplicate_id, 'dismissed')"
                    class="btn btn-sm btn-success"
                    :class="{ 'active': duplicate.user_validation?.status === 'dismissed' }"
                  >
                    <i class="fas fa-times-circle mr-1"></i>
                    Not Duplicate
                  </button>
                  <button 
                    @click="validateDuplicate(duplicate.duplicate_id, 'needs_review')"
                    class="btn btn-sm btn-warning"
                    :class="{ 'active': duplicate.user_validation?.status === 'needs_review' }"
                  >
                    <i class="fas fa-eye mr-1"></i>
                    Needs Review
                  </button>
                </div>
                <textarea 
                  v-model="duplicate.user_validation.validation_notes"
                  class="form-control mt-2"
                  rows="2"
                  placeholder="Add your validation notes..."
                  @blur="saveValidationNotes(duplicate.duplicate_id)"
                ></textarea>
              </div>
            </div>

            <!-- Recommended Action -->
            <div v-if="duplicate.recommended_action" class="action-section">
              <h6>📋 Recommended Action</h6>
              <div class="action-content">
                <i class="fas fa-tasks text-primary mr-2"></i>
                {{ duplicate.recommended_action }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Medium Confidence Duplicates -->
      <div v-if="getParsedMediumConfidence().length > 0" class="duplicates-section medium-confidence">
        <h4 class="section-title">
          <i class="fas fa-question-circle text-warning mr-2"></i>
          Medium Confidence Duplicates ({{ getParsedMediumConfidence().length }})
        </h4>
        
        <div class="duplicate-cards">
          <div 
            v-for="duplicate in getParsedMediumConfidence()" 
            :key="duplicate.duplicate_id"
            class="duplicate-card medium"
          >
            <!-- Same structure as high confidence but with medium styling -->
            <div class="duplicate-header">
              <div class="duplicate-title">
                <h5>{{ duplicate.title || 'Potential Duplicate' }}</h5>
                <div class="duplicate-badges">
                  <span class="type-badge">{{ formatDuplicateType(duplicate.type) }}</span>
                  <span class="confidence-badge medium">{{ Math.round(duplicate.confidence * 100) }}%</span>
                  <span v-if="duplicate.cost_impact" class="impact-badge">
                    ${{ duplicate.cost_impact.toLocaleString() }}
                  </span>
                </div>
              </div>
            </div>
            <!-- Include all the same sections as high confidence -->
          </div>
        </div>
      </div>

      <!-- Recommendations -->
      <div v-if="results.structured_results?.recommendations?.length" class="recommendations-section">
        <h4 class="section-title">
          <i class="fas fa-lightbulb text-info mr-2"></i>
          Recommendations
        </h4>
        <ul class="recommendations-list">
          <li v-for="rec in results.structured_results.recommendations" :key="rec">
            {{ rec }}
          </li>
        </ul>
      </div>
    </div>

    <!-- Error Display -->
    <div v-if="error" class="error-section">
      <div class="alert alert-danger">
        <i class="fas fa-exclamation-circle mr-2"></i>
        {{ error }}
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import api from '@/services/api'
import { useToast } from 'vue-toastification'

export default {
  name: 'DuplicateDetection',
  setup() {
    const toast = useToast()
    
    // Reactive data
    const connections = ref([])
    const selectedConnection = ref(null)
    const houseNumber = ref('')
    const analysisFocus = ref('general')
    const analyzing = ref(false)
    const results = ref(null)
    const error = ref(null)
    
    // Load database connections
    const loadConnections = async () => {
      try {
        const response = await api.get('/database-connections/')
        connections.value = response.data
      } catch (err) {
        console.error('Error loading connections:', err)
        toast.error('Failed to load database connections')
      }
    }
    
    // Analyze duplicates
    const analyzeDuplicates = async () => {
      if (!selectedConnection.value || !houseNumber.value) return
      
      analyzing.value = true
      results.value = null
      error.value = null
      
      try {
        console.log('🔍 Starting duplicate analysis...')
        console.log('Connection:', selectedConnection.value.name)
        console.log('House:', houseNumber.value)
        console.log('Focus:', analysisFocus.value)
        
        const response = await api.post(
          `/database-connections/${selectedConnection.value.id}/analyze_house_duplicates/`,
          {
            house_number: houseNumber.value,
            analysis_focus: analysisFocus.value
          }
        )
        
        console.log('📊 Raw API Response:', response.data)
        
        if (response.data.success) {
          results.value = response.data
          console.log('✅ Analysis successful')
          console.log('Structured results:', response.data.structured_results)
          console.log('Raw analysis:', response.data.raw_analysis)
          
          toast.success(`Analysis complete! Found ${response.data.structured_results?.duplicates_found || 0} potential duplicates`)
        } else {
          error.value = response.data.error
          toast.error(`Analysis failed: ${response.data.error}`)
        }
        
      } catch (err) {
        console.error('❌ Duplicate analysis error:', err)
        error.value = err.response?.data?.error || err.message
        toast.error(`Analysis failed: ${err.response?.data?.error || err.message}`)
      } finally {
        analyzing.value = false
      }
    }
    
    // Parse high confidence duplicates
    const getParsedHighConfidence = () => {
      if (!results.value) {
        console.error('❌ No results available for parsing')
        return []
      }
      
      console.log('🔍 Parsing high confidence duplicates...')
      
      // Check if we have structured results
      if (results.value.structured_results?.high_confidence_duplicates) {
        console.log('✅ Found in structured_results:', results.value.structured_results.high_confidence_duplicates)
        return results.value.structured_results.high_confidence_duplicates
      }
      
      // If no structured results, this is an error condition
      if (!results.value.structured_results) {
        console.error('❌ CRITICAL: No structured_results in response')
        console.error('Response structure:', Object.keys(results.value))
        error.value = 'Response parsing failed: No structured_results found. Check debug section for raw response.'
        return []
      }
      
      // If structured_results exists but no high_confidence_duplicates
      console.log('ℹ️ No high confidence duplicates found in structured results')
      return []
    }
    
    // Parse medium confidence duplicates
    const getParsedMediumConfidence = () => {
      if (!results.value) {
        console.error('❌ No results available for parsing')
        return []
      }
      
      // Check if we have structured results
      if (results.value.structured_results?.medium_confidence_duplicates) {
        return results.value.structured_results.medium_confidence_duplicates
      }
      
      // If no structured results, this is an error condition
      if (!results.value.structured_results) {
        console.error('❌ CRITICAL: No structured_results in response')
        return []
      }
      
      // If structured_results exists but no medium_confidence_duplicates
      console.log('ℹ️ No medium confidence duplicates found in structured results')
      return []
    }
    
    // Removed parseFromRawAnalysis - no more fallback parsing
    // If structured_results is missing, that's an error that needs to be fixed
    
    // Get total cost impact
    const getTotalCostImpact = () => {
      const high = getParsedHighConfidence()
      const medium = getParsedMediumConfidence()
      
      const total = [...high, ...medium].reduce((sum, dup) => sum + (dup.cost_impact || 0), 0)
      return total
    }
    
    // Format currency
    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('en-US').format(amount || 0)
    }
    
    // Format duplicate type
    const formatDuplicateType = (type) => {
      const typeMap = {
        'critical_duplicate': 'Critical Duplicate (Same Cost Code + Vendor)',
        'exact_duplicate': 'Exact Duplicate',
        'near_duplicate': 'Near Duplicate',
        'same_work_different_vendors': 'Same Work, Different Vendors',
        'amount_variation': 'Amount Variation',
        'timeline_overlap': 'Timeline Overlap',
        'cross_table_duplicate': 'Cross-Table Duplicate'
      }
      return typeMap[type] || type
    }
    
    // Validate duplicate
    const validateDuplicate = (duplicateId, status) => {
      console.log(`🔍 Validating duplicate ${duplicateId} as ${status}`)
      
      // Find the duplicate in either high or medium confidence
      const allDuplicates = [...getParsedHighConfidence(), ...getParsedMediumConfidence()]
      const duplicate = allDuplicates.find(d => d.duplicate_id === duplicateId)
      
      if (duplicate) {
        if (!duplicate.user_validation) {
          duplicate.user_validation = {}
        }
        
        duplicate.user_validation.status = status
        duplicate.user_validation.validated_at = new Date().toISOString()
        duplicate.user_validation.false_positive = (status === 'dismissed')
        
        toast.success(`Duplicate marked as ${status}`)
      } else {
        console.error('❌ Duplicate not found:', duplicateId)
      }
    }
    
    // Save validation notes
    const saveValidationNotes = (duplicateId) => {
      console.log(`💾 Saving validation notes for ${duplicateId}`)
      toast.info('Validation notes saved')
    }
    
    // Lifecycle
    onMounted(() => {
      loadConnections()
    })
    
    return {
      // Data
      connections,
      selectedConnection,
      houseNumber,
      analysisFocus,
      analyzing,
      results,
      error,
      
      // Methods
      analyzeDuplicates,
      getParsedHighConfidence,
      getParsedMediumConfidence,
      getTotalCostImpact,
      formatCurrency,
      formatDuplicateType,
      validateDuplicate,
      saveValidationNotes
    }
  }
}
</script>

<style scoped>
.duplicate-detection-page {
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

.page-header {
  text-align: center;
  margin-bottom: 2rem;
}

.page-header h2 {
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.page-description {
  color: #7f8c8d;
  font-size: 1.1rem;
}

.connection-section,
.analysis-form {
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  margin-bottom: 2rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 1rem;
  align-items: end;
}

.form-group label {
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.5rem;
  display: block;
}

.summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.summary-card {
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  text-align: center;
}

.card-value {
  font-size: 2rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.card-label {
  color: #7f8c8d;
  font-size: 0.9rem;
}

.debug-section {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 2rem;
  border: 1px solid #e9ecef;
}

.debug-toggle {
  cursor: pointer;
  color: #6c757d;
  font-weight: 500;
}

.debug-content {
  margin-top: 1rem;
}

.debug-json {
  background: #2d3748;
  color: #e2e8f0;
  padding: 1rem;
  border-radius: 4px;
  font-size: 0.8rem;
  max-height: 300px;
  overflow-y: auto;
  margin-bottom: 1rem;
}

.analysis-summary {
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  margin-bottom: 2rem;
}

.duplicates-section {
  margin-bottom: 2rem;
}

.section-title {
  color: #2c3e50;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e9ecef;
}

.duplicate-cards {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.duplicate-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  border-left: 4px solid #dc3545;
}

.duplicate-card.medium {
  border-left-color: #ffc107;
}

.duplicate-header {
  margin-bottom: 1rem;
}

.duplicate-title h5 {
  color: #2c3e50;
  margin: 0 0 0.5rem 0;
  font-size: 1.2rem;
}

.duplicate-badges {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.type-badge,
.confidence-badge,
.impact-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
}

.type-badge {
  background: #e9ecef;
  color: #495057;
}

.confidence-badge.high {
  background: #dc3545;
  color: white;
}

.confidence-badge.medium {
  background: #ffc107;
  color: #212529;
}

.impact-badge {
  background: #17a2b8;
  color: white;
}

.records-section,
.analysis-section,
.validation-section,
.user-validation-section,
.action-section {
  margin: 1.5rem 0;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
}

.records-section h6,
.analysis-section h6,
.validation-section h6,
.user-validation-section h6,
.action-section h6 {
  color: #495057;
  margin-bottom: 1rem;
  font-size: 1rem;
}

.records-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
}

.record-card {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1rem;
}

.record-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e9ecef;
}

.table-badge {
  background: #007bff;
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.record-id {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.85rem;
  color: #6c757d;
}

.record-fields {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.field-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.9rem;
}

.field-label {
  font-weight: 500;
  color: #495057;
  text-transform: capitalize;
}

.field-value {
  color: #2c3e50;
}

.analysis-grid {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.analysis-item {
  padding: 0.75rem;
  background: white;
  border-radius: 6px;
  border-left: 3px solid #007bff;
}

.analysis-item strong {
  color: #2c3e50;
  display: block;
  margin-bottom: 0.5rem;
}

.analysis-item p {
  margin: 0;
  color: #495057;
  line-height: 1.4;
}

.indicators-section,
.risks-section {
  margin-top: 1rem;
  padding: 0.75rem;
  background: white;
  border-radius: 6px;
}

.indicators-list,
.risks-list {
  margin: 0.5rem 0 0 1.5rem;
  padding: 0;
}

.indicators-list li {
  color: #007bff;
  margin-bottom: 0.25rem;
}

.risks-list li {
  color: #dc3545;
  margin-bottom: 0.25rem;
}

.questions-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.question-item {
  background: white;
  padding: 0.75rem;
  border-radius: 6px;
  border-left: 3px solid #17a2b8;
  color: #495057;
  line-height: 1.4;
}

.validation-controls {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.validation-buttons {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.validation-buttons .btn.active {
  box-shadow: 0 0 0 2px rgba(0,123,255,0.25);
  transform: scale(1.02);
}

.action-content {
  background: white;
  padding: 1rem;
  border-radius: 6px;
  border-left: 3px solid #28a745;
  color: #495057;
  line-height: 1.4;
}

.recommendations-section {
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.recommendations-list {
  margin: 1rem 0 0 1.5rem;
  padding: 0;
}

.recommendations-list li {
  color: #495057;
  margin-bottom: 0.5rem;
  line-height: 1.4;
}

.error-section {
  margin-top: 2rem;
}

/* Responsive Design */
@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .summary-cards {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .records-grid {
    grid-template-columns: 1fr;
  }
  
  .validation-buttons {
    flex-direction: column;
  }
}
</style>
